import { Paper, Typography, TextField, Grid, IconButton, Button, Select, Checkbox, MenuItem, Box } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import skillsData from '../data/skills.json'
import characteristics from '../data/characteristics.json'
import SymbolField from '../Util/SymbolField'
import NpcProfileEditor from './NpcProfileEditor'
import { RollModEditor, IntegratedWeaponsEditor, BuiltInAttachmentsField, CategoriesField, weaponQualityOptions, OrdnanceField } from './ModEditors'

const appFiles = JSON.parse(localStorage.getItem('appFiles') || '{}');
let skills = skillsData
if (appFiles.skills) { skills = JSON.parse(appFiles.skills) }

const ItemEdit = ({ editedItem, index, category, appFiles, setAppFiles }) => {
    const qualities = JSON.parse(appFiles.qualities)
    const talents = JSON.parse(appFiles.talents)
    const attachments = appFiles.attachments ? JSON.parse(appFiles.attachments) : []
    const attachmentType = category === "weapons" ? (editedItem?.Type === "Vehicle" ? "Vehicle" : "Weapon") : category === "armor" ? "Armor" : category === "gear" ? "Gear" : null
    const filteredQualities = qualities.filter(q => !q.Type || (q.Type && (category === "weapons" ? ["Weapon", "Gear"] : ["Armor", "Gear"]).includes(q.Type)))

    const handleEdit = (newItem) => {
        let newFiles = { ...appFiles };
        let newCat = JSON.parse(appFiles[category]);
        newCat[index] = newItem;
        newFiles[category] = JSON.stringify(newCat);
        setAppFiles(newFiles);
        localStorage.setItem('appFiles', JSON.stringify(newFiles));
    };

    if (editedItem.BaseMods && !Array.isArray(editedItem.BaseMods)) { editedItem.BaseMods = [editedItem.BaseMods] }
    const itemQualities = Array.isArray(editedItem.Qualities) ? editedItem.Qualities : editedItem.Qualities ? [editedItem.Qualities] : []
    const qualityOptions = weaponQualityOptions(qualities)
    const setQualities = (next) => handleEdit({ ...editedItem, Qualities: next })

    const usesBrawn = editedItem?.DamageAdd !== undefined
    const damageValue = usesBrawn ? (editedItem?.DamageAdd ?? "") : (editedItem?.Damage ?? "")
    const setDamageValue = (v) => {
        const next = { ...editedItem }
        if (usesBrawn) { next.DamageAdd = v; delete next.Damage }
        else { next.Damage = v; delete next.DamageAdd }
        handleEdit(next)
    }
    const toggleBrawn = (checked) => {
        const next = { ...editedItem }
        const carried = damageValue
        if (checked) { next.DamageAdd = carried; delete next.Damage }
        else { next.Damage = carried; delete next.DamageAdd }
        handleEdit(next)
    }

    return (
        <Paper>
            <TextField
                sx={{
                    marginTop: "10px"
                }}
                fullWidth
                label="Image URL"
                value={editedItem?.imageUrl || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, imageUrl: e.target.value })
                }
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Price"
                value={editedItem?.Price || 0}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Price: e.target.value })
                }
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Rarity"
                value={editedItem?.Rarity || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Rarity: e.target.value })
                }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                <span>Restricted:</span>
                <Checkbox
                    checked={!!editedItem?.Restricted}
                    onChange={(e) => handleEdit({ ...editedItem, Restricted: e.target.checked })}
                />
                <span style={{ marginLeft: "20px" }}>Unique Item:</span>
                <Checkbox
                    checked={!!editedItem?.UniqueItem}
                    onChange={(e) => handleEdit({ ...editedItem, UniqueItem: e.target.checked ? "true" : undefined })}
                />
                {category === "weapons" && (
                    <>
                        <span style={{ marginLeft: "20px" }}>Two-handed:</span>
                        <Checkbox
                            checked={editedItem?.Hands === "whTwoHanded"}
                            onChange={(e) => handleEdit({ ...editedItem, Hands: e.target.checked ? "whTwoHanded" : undefined })}
                        />
                    </>
                )}
            </Box>
            {category !== "armor" && (
                <CategoriesField
                    categories={editedItem?.Categories}
                    resetKey={index}
                    sx={{ marginTop: "10px" }}
                    onCommit={(next) => handleEdit({ ...editedItem, Categories: next })}
                />
            )}
            {category === "gear" && (
                <TextField
                    sx={{ marginTop: "10px", maxWidth: "220px" }}
                    label="Cyber Capacity Bonus"
                    value={editedItem?.CyberCapacityBonus ?? ""}
                    onChange={(e) => { if (!isNaN(e.target.value)) { handleEdit({ ...editedItem, CyberCapacityBonus: e.target.value || undefined }) } }}
                />
            )}
            <BuiltInAttachmentsField
                value={editedItem?.BuiltInAttachments}
                onChange={(v) => handleEdit({ ...editedItem, BuiltInAttachments: v })}
                attachments={attachments}
                type={attachmentType}
            />
            {category === "weapons" && editedItem?.Type === "Vehicle" && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                        <span>Ordnance (loaded into launchers):</span>
                        <Checkbox
                            checked={editedItem?.Ordnance === true || editedItem?.Ordnance === "true"}
                            onChange={(e) => handleEdit({ ...editedItem, Ordnance: e.target.checked ? "true" : undefined })}
                        />
                    </Box>
                    <OrdnanceField
                        value={editedItem?.OrdnanceKeys}
                        onChange={(v) => handleEdit({ ...editedItem, OrdnanceKeys: v })}
                        weapons={appFiles.weapons ? JSON.parse(appFiles.weapons) : []}
                    />
                </>
            )}
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                }}
                label="Encumbrance"
                value={editedItem?.Encumbrance || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Encumbrance: e.target.value })
                }
            />
            {category === "weapons" && (
                <Box sx={{ display: "flex", alignItems: "center", marginTop: "10px" }}>
                    <Typography sx={{ marginRight: "10px" }}>Skill:</Typography>
                    <Select
                        value={editedItem?.SkillKey || ''}
                        onChange={(e) => handleEdit({ ...editedItem, SkillKey: e.target.value })}
                        sx={{ minWidth: "200px" }}
                        displayEmpty
                    >
                        <MenuItem value="">(none)</MenuItem>
                        {skills.slice().sort((a, b) => (a.skill || "").localeCompare(b.skill || "")).map((skill) => (
                            <MenuItem key={skill.Key} value={skill.Key}>
                                {skill.skill}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}
            {category === "weapons" && (
                <Box sx={{ display: "flex", alignItems: "center", marginTop: "10px", width: "50%" }}>
                    <TextField
                        fullWidth
                        label={usesBrawn ? "Damage (Brawn +)" : "Damage"}
                        value={damageValue}
                        onChange={(e) => setDamageValue(e.target.value)}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", marginLeft: "10px", whiteSpace: "nowrap" }}>
                        <span>+Brawn:</span>
                        <Checkbox
                            checked={usesBrawn}
                            onChange={(e) => toggleBrawn(e.target.checked)}
                        />
                    </Box>
                </Box>
            )}
            {category === "weapons" && (
                <TextField
                    sx={{
                        marginTop: "10px",
                        width: "50%",
                    }}
                    label="Crit"
                    value={editedItem?.Crit || ""}
                    onChange={(e) =>
                        handleEdit({ ...editedItem, Crit: e.target.value })
                    }
                />
            )}
            {category === "weapons" && (
                <TextField
                    fullWidth
                    sx={{
                        marginTop: "10px",
                    }}
                    label="Range"
                    value={editedItem?.Range || ""}
                    onChange={(e) =>
                        handleEdit({ ...editedItem, Range: e.target.value })
                    }
                />
            )}
            {category === "armor" && (
                <TextField
                    sx={{
                        marginTop: "10px",
                        width: "50%",
                    }}
                    label="Soak"
                    value={editedItem?.Soak || ""}
                    onChange={(e) =>
                        handleEdit({ ...editedItem, Soak: e.target.value })
                    }
                />
            )}
            {category === "armor" && (
                <TextField
                    sx={{
                        marginTop: "10px",
                        width: "50%",
                    }}
                    label="Defense"
                    value={editedItem?.Defense || ""}
                    onChange={(e) =>
                        handleEdit({ ...editedItem, Defense: e.target.value })
                    }
                />
            )}
            {(category === "armor" || category === "weapons") && (
                <TextField
                    fullWidth
                    sx={{
                        marginTop: "10px",
                    }}
                    label="HP"
                    value={editedItem?.HP || ""}
                    onChange={(e) =>
                        handleEdit({ ...editedItem, HP: e.target.value })
                    }
                />
            )}
            {(category === "gear" || category === "weapons") && (
                <TextField
                    fullWidth
                    sx={{
                        marginTop: "10px",
                    }}
                    label="Type"
                    value={editedItem?.Type || ""}
                    onChange={(e) =>
                        handleEdit({ ...editedItem, Type: e.target.value })
                    }
                />
            )}
            {category === "weapons" && editedItem?.Type === "Vehicle" && (
                <Box sx={{ display: 'flex' }}>
                    <TextField
                        sx={{ marginTop: "10px", width: "50%" }}
                        label="Min Silhouette"
                        value={editedItem?.SizeLow || ""}
                        onChange={(e) => handleEdit({ ...editedItem, SizeLow: e.target.value })}
                    />
                    <TextField
                        sx={{ marginTop: "10px", width: "50%" }}
                        label="Max Silhouette"
                        value={editedItem?.SizeHigh || ""}
                        onChange={(e) => handleEdit({ ...editedItem, SizeHigh: e.target.value })}
                    />
                </Box>
            )}
            <Typography>Qualities:</Typography>
            <Grid container>
                {itemQualities.map((quality, qI) => (
                    <Grid item key={qI} sx={{ margin: "7.5px", display: "flex", alignItems: "center" }}>
                        <Select
                            value={quality.Key || ""}
                            displayEmpty
                            onChange={(e) => setQualities(itemQualities.map((q, i) => (i === qI ? { ...q, Key: e.target.value } : q)))}
                            sx={{ minWidth: "180px" }}
                        >
                            <MenuItem value=""><em>Quality</em></MenuItem>
                            {quality.Key && !qualityOptions.some(q => q.Key === quality.Key) &&
                                <MenuItem value={quality.Key}>{qualities.find(q => q.Key === quality.Key)?.Name || quality.Key}</MenuItem>
                            }
                            {qualityOptions.map((q) => (<MenuItem key={q.Key} value={q.Key}>{q.Name}</MenuItem>))}
                        </Select>
                        <TextField
                            label="Rating"
                            value={quality.Count || ""}
                            onChange={(e) => setQualities(itemQualities.map((q, i) => {
                                if (i !== qI) { return q; }
                                const c = { ...q };
                                if (e.target.value === "") { delete c.Count; } else { c.Count = e.target.value; }
                                return c;
                            }))}
                            sx={{ maxWidth: "80px", marginLeft: "4px" }}
                        />
                        <IconButton
                            onClick={() => setQualities(itemQualities.filter((_, i) => i !== qI))}
                            sx={{ maxWidth: "30px" }}
                            size="small"
                            className="no-print"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                ))}
            </Grid>
            <Button onClick={() => setQualities([...itemQualities, {}])} className="no-print">
                Add {attachmentType || "Item"} Quality
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px' }}>
                <span>Skill Choice on Purchase:</span>
                <Checkbox
                    checked={!!editedItem.SkillChoice}
                    onChange={(e) => {
                        const next = { ...editedItem }
                        if (e.target.checked) { next.SkillChoice = { Skills: [], SkillIsCareer: true } } else { delete next.SkillChoice }
                        handleEdit(next)
                    }}
                />
                {editedItem.SkillChoice && (
                    <>
                        <Select
                            multiple
                            displayEmpty
                            value={editedItem.SkillChoice.Skills || []}
                            onChange={(e) => handleEdit({ ...editedItem, SkillChoice: { ...editedItem.SkillChoice, Skills: e.target.value } })}
                            renderValue={(sel) => sel.length ? sel.map((k) => skills.find((s) => s.Key === k)?.skill || k).join(", ") : <em>Any Skill</em>}
                            sx={{ minWidth: "220px", maxWidth: "500px" }}
                        >
                            {[...skills].sort((a, b) => (a.skill || "").localeCompare(b.skill || "")).map((s) => (
                                <MenuItem key={s.Key} value={s.Key}>{s.skill}</MenuItem>
                            ))}
                        </Select>
                        <span style={{ marginLeft: "10px" }}>Make Career:</span>
                        <Checkbox
                            checked={!!editedItem.SkillChoice.SkillIsCareer}
                            onChange={(e) => handleEdit({ ...editedItem, SkillChoice: { ...editedItem.SkillChoice, SkillIsCareer: e.target.checked } })}
                        />
                        <TextField
                            label="Ranks"
                            value={editedItem.SkillChoice.Count || ""}
                            onChange={(e) => {
                                if (isNaN(e.target.value)) { return }
                                const choice = { ...editedItem.SkillChoice, Count: e.target.value }
                                if (e.target.value === "") { delete choice.Count }
                                handleEdit({ ...editedItem, SkillChoice: choice })
                            }}
                            sx={{ maxWidth: "90px" }}
                        />
                    </>
                )}
            </Box>
            <Typography>Base Mods:</Typography>
            {editedItem.BaseMods && (
                <Grid container>
                    {editedItem.BaseMods.map((mod, index) => (
                        <Grid item key={index} sx={{ margin: "7.5px" }}>
                            {(mod.MiscDesc || mod.MiscDesc === "") &&
                                <>
                                    <Typography>Custom Mod:</Typography>
                                    <SymbolField
                                        label="Description"
                                        sx={{ minWidth: "500px" }}
                                        value={mod.MiscDesc}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].MiscDesc = e.target.value;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                    />
                                </>
                            }
                            {mod.Key && skills.find(s => s.Key === mod.Key) &&
                                <>
                                    <Typography>Skill:</Typography>
                                    <Select
                                        value={mod.Key || ''}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].Key = e.target.value;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                    >
                                        {skills.sort((a, b) => (a.skill || "").localeCompare(b.skill || "")).map((skill) => (
                                            <MenuItem key={skill.Key} value={skill.Key}>
                                                {skill.skill}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <TextField
                                        label="Ranks"
                                        value={mod.Count || 0}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].Count = !isNaN(e.target.value) ? e.target.value : newMods[index].Count || 0;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                        sx={{ maxWidth: "90px" }}
                                    />
                                    {" "} Make Career:
                                    <Checkbox
                                        checked={!!mod.SkillIsCareer}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].SkillIsCareer = e.target.checked;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                    />
                                </>
                            }
                            {mod.DieModifiers &&
                                <RollModEditor
                                    dieModifiers={mod.DieModifiers}
                                    skills={skills}
                                    onChange={(newDieMods) => {
                                        let newMods = [...editedItem.BaseMods];
                                        newMods[index] = { ...newMods[index], DieModifiers: newDieMods };
                                        handleEdit({ ...editedItem, BaseMods: newMods });
                                    }}
                                />
                            }
                            {mod.Key && talents.find(t => t.Key === mod.Key) &&
                                <>
                                    <Typography>Innate Talent:</Typography>
                                    <Select
                                        value={mod.Key || ''}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].Key = e.target.value;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                    >
                                        {talents.sort((a, b) => a.Name.localeCompare(b.Name)).map((talent) => (
                                            <MenuItem key={talent.Key} value={talent.Key}>
                                                {talent.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <TextField
                                        label="Ranks"
                                        value={mod.Count || 0}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].Count = !isNaN(e.target.value) ? e.target.value : newMods[index].Count || 0;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                        sx={{ maxWidth: "90px" }}
                                    />
                                </>
                            }
                            {mod.Key && characteristics.find(t => t.Key === mod.Key) &&
                                <>
                                    <Typography>Characteristic Mod:</Typography>
                                    <Select
                                        value={mod.Key || ''}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].Key = e.target.value;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                        sx={{ maxWidth: "180px" }}
                                    >
                                        {characteristics.map((characteristic) => (
                                            <MenuItem key={characteristic.Key} value={characteristic.Key}>
                                                {characteristic.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <TextField
                                        label="Ranks"
                                        value={mod.Count || 0}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].Count = !isNaN(e.target.value) ? e.target.value : newMods[index].Count || 0;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                        sx={{ maxWidth: "90px" }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <span>Doesn't affect Soak/Wound Threshold:</span>
                                        <Checkbox
                                            checked={!!mod.NoSoakWound}
                                            onChange={(e) => {
                                                let newMods = [...editedItem.BaseMods];
                                                newMods[index] = { ...newMods[index], NoSoakWound: e.target.checked || undefined };
                                                handleEdit({ ...editedItem, BaseMods: newMods });
                                            }}
                                        />
                                    </Box>
                                </>
                            }
                            {mod.Key && qualities.find(t => t.Key === mod.Key) &&
                                <>
                                    <Typography>Quality:</Typography>
                                    <Select
                                        value={mod.Key || ''}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].Key = e.target.value;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                    >
                                        {!filteredQualities.some(q => q.Key === mod.Key) &&
                                            <MenuItem value={mod.Key}>{qualities.find(q => q.Key === mod.Key)?.Name || mod.Key}</MenuItem>
                                        }
                                        {filteredQualities.sort((a, b) => a.Name.localeCompare(b.Name)).map((quality) => (
                                            <MenuItem key={quality.Key} value={quality.Key}>
                                                {quality.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <TextField
                                        label="Ranks"
                                        value={mod.Count || 0}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].Count = !isNaN(e.target.value) ? e.target.value : newMods[index].Count || 0;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                        sx={{ maxWidth: "90px" }}
                                    />
                                </>
                            }
                            <IconButton onClick={(e) => {
                                let newMods = [...editedItem.BaseMods];
                                newMods.splice(index, 1)
                                handleEdit({ ...editedItem, BaseMods: newMods });
                            }}
                                sx={{ maxWidth: "30px" }}
                                size="small"
                                className="no-print"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    ))}
                </Grid>
            )}
            <Button onClick={(e) => {
                let newMods = [...editedItem.BaseMods || []];
                newMods.push({ MiscDesc: "" })
                handleEdit({ ...editedItem, BaseMods: newMods });
            }}
                className="no-print"
            >
                Add Custom Mod
            </Button>
            <Button onClick={(e) => {
                let newMods = [...(editedItem.BaseMods || [])];
                newMods.push({ Key: "BR" })
                handleEdit({ ...editedItem, BaseMods: newMods });
            }}
                className="no-print"
            >
                Add Characteristic Mod
            </Button>
            <Button onClick={(e) => {
                let newMods = [...(editedItem.BaseMods || [])];
                newMods.push({ Key: "ASTRO" })
                handleEdit({ ...editedItem, BaseMods: newMods });
            }}
                className="no-print"
            >
                Add Skill Mod
            </Button>
            <Button onClick={(e) => {
                let newMods = [...(editedItem.BaseMods || [])];
                newMods.push({ DieModifiers: [{ SkillKey: skills[0]?.Key || "" }] })
                handleEdit({ ...editedItem, BaseMods: newMods });
            }}
                className="no-print"
            >
                Add Roll Mod
            </Button>
            <Button onClick={(e) => {
                let newMods = [...(editedItem.BaseMods || [])];
                newMods.push({ Key: "ADV" })
                handleEdit({ ...editedItem, BaseMods: newMods });
            }}
                className="no-print"
            >
                Add Talent Mod
            </Button>
            <Button onClick={(e) => {
                let newMods = [...(editedItem.BaseMods || [])];
                newMods.push({ Key: filteredQualities[0].Key })
                handleEdit({ ...editedItem, BaseMods: newMods });
            }}
                className="no-print"
            >
                Add Quality Mod
            </Button>
            <IntegratedWeaponsEditor
                weaponModifiers={editedItem.WeaponModifiers || []}
                skills={skills}
                qualities={qualities}
                onChange={(newWeapons) => handleEdit({ ...editedItem, WeaponModifiers: newWeapons.length ? newWeapons : undefined })}
            />
            {(category === "gear" || editedItem.Profile) &&
                <NpcProfileEditor
                    profile={editedItem.Profile}
                    itemName={editedItem.Name}
                    skills={skills}
                    talents={talents}
                    qualities={qualities}
                    weapons={appFiles.weapons ? JSON.parse(appFiles.weapons) : []}
                    onChange={(profile) => {
                        const next = { ...editedItem, Profile: profile }
                        if (!profile) { delete next.Profile }
                        handleEdit(next)
                    }}
                />
            }
        </Paper>
    )
}

export default ItemEdit